import { FormControl, FormLabel, Select, useId } from "@chakra-ui/react";
import { useAtom } from "jotai";
import { atomWithStorage, createJSONStorage } from "jotai/utils";
import { useCallback } from "react";
import { pdfRecordAtom } from "../pages/ComparePage";

export enum TaxKey {
  Facade = "facade",
  Floor = "floor",
  Frame = "frame",
  Heating = "heating",
  Roof = "roof",
  Ventilation = "ventilation",
}

const taxOptions = [9, 18, 21] as const;

export type TaxValue = (typeof taxOptions)[number];

export const defaultTaxValue: TaxValue = 18;

export const taxMapAtom = atomWithStorage<
  Record<number, Record<TaxKey, TaxValue>>
>(
  "scenarioTaxMap",
  {},
  createJSONStorage(() => sessionStorage),
);

export const calculateTax = (price: number, tax: TaxValue) =>
  price * (tax / 100);

const isTaxValue = (value: number): value is TaxValue =>
  (taxOptions as readonly number[]).includes(value);

interface Props {
  value: TaxValue;
  scenarioId: number;
  taxKey: TaxKey;
}

export function TaxSelect({ value, scenarioId, taxKey }: Props) {
  const id = useId();
  const [, setTaxMap] = useAtom(taxMapAtom);
  const [recordingPdf] = useAtom(pdfRecordAtom);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const newValue = Number(e.target.value);
      if (isTaxValue(newValue)) {
        setTaxMap((prev) => ({
          ...prev,
          [scenarioId]: {
            ...prev[scenarioId],
            [taxKey]: newValue,
          },
        }));
      }
    },
    [scenarioId, taxKey, setTaxMap],
  );

  return (
    <FormControl alignItems="center" display="flex" gap={recordingPdf ? 0 : 2}>
      <Select
        ref={(r) => {
          if (r) r.style.width = "unset";
        }}
        borderRadius={4}
        className={recordingPdf ? "hide-border" : ""}
        id={id}
        mx={1}
        onChange={handleChange}
        size="xs"
        value={value}
        variant="outline"
        w={recordingPdf ? "unset" : 16}
      >
        <option value={9}>9%</option>
        <option value={18}>18%</option>
        <option value={21}>21%</option>
      </Select>
      <FormLabel htmlFor={id}>BTW</FormLabel>
    </FormControl>
  );
}
